import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { staffAPI, scheduleAPI, timesheetAPI } from '../lib/api';

const STEPS = [
  { key: 'employee',  label: 'Add an employee',      cta: 'Add now',  to: '/staff'                },
  { key: 'shift',     label: 'Create a shift',        cta: 'Schedule', to: '/schedule'             },
  { key: 'timesheet', label: 'View timesheets',       cta: 'Open',     to: '/timesheets'           },
  { key: 'dashboard', label: 'Open labor dashboard',  cta: 'View',     to: '/executive-insights'   },
];

const lsKey = (uid) => `restaff_ob_${uid}`;

/** Call from any page to mark a step complete. */
export function markOnboarding(uid, step) {
  if (!uid) return;
  try {
    const raw = localStorage.getItem(lsKey(uid));
    const cur = raw ? JSON.parse(raw) : {};
    if (cur[step]) return; // already done — skip event spam
    cur[step] = true;
    localStorage.setItem(lsKey(uid), JSON.stringify(cur));
    window.dispatchEvent(new CustomEvent('ob-update'));
  } catch {}
}

export default function OnboardingChecklist() {
  const { user } = useAuth();
  const [steps, setSteps] = useState(null); // null = not yet loaded

  const uid          = user?.id || user?._id || user?.email;
  const restaurantId = user?.restaurant_id;

  const load = useCallback(async () => {
    if (!uid || !restaurantId) return;

    let saved = {};
    try {
      const raw = localStorage.getItem(lsKey(uid));
      saved = raw ? JSON.parse(raw) : {};
    } catch {}

    const updates = { ...saved };

    // API-based checks for steps not yet marked done
    if (!updates.employee) {
      try {
        const r = await staffAPI.getAll(restaurantId);
        if ((r.data?.length || 0) > 0) updates.employee = true;
      } catch {}
    }

    if (!updates.shift) {
      try {
        const r = await scheduleAPI.getAll(restaurantId, {});
        if ((r.data?.length || 0) > 0) updates.shift = true;
      } catch {}
    }

    if (!updates.timesheet) {
      try {
        const r = await timesheetAPI.getEntries({ limit: 1 });
        const entries = Array.isArray(r.data) ? r.data : [];
        if (entries.length > 0) updates.timesheet = true;
      } catch {}
    }

    try {
      localStorage.setItem(lsKey(uid), JSON.stringify(updates));
    } catch {}

    setSteps(updates);
  }, [uid, restaurantId]);

  useEffect(() => { load(); }, [load]);

  // Re-check when another page fires ob-update (timesheet visited, labor viewed)
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('ob-update', handler);
    return () => window.removeEventListener('ob-update', handler);
  }, [load]);

  // Not loaded yet, or all done → render nothing
  if (!steps) return null;
  const doneCount = STEPS.filter(s => steps[s.key]).length;
  if (doneCount === 4) return null;

  const progress = Math.round((doneCount / 4) * 100);

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-4"
      data-testid="onboarding-checklist"
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-slate-800">
          Get started — {doneCount}/4 complete
        </p>
        <div className="flex items-center gap-2">
          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-orange-500 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-slate-400">{progress}%</span>
        </div>
      </div>

      {/* Steps grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {STEPS.map((step) => {
          const done = !!steps[step.key];
          return (
            <div
              key={step.key}
              data-testid={`ob-step-${step.key}`}
              className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${
                done ? 'bg-slate-50 border-slate-100' : 'bg-white border-slate-200'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {done
                  ? <CheckCircle2 size={14} className="text-teal-500 flex-shrink-0" />
                  : <Circle      size={14} className="text-slate-300 flex-shrink-0" />
                }
                <span className={`text-xs truncate ${
                  done ? 'text-slate-400 line-through' : 'text-slate-700 font-medium'
                }`}>
                  {step.label}
                </span>
              </div>

              {!done && (
                <Link
                  to={step.to}
                  data-testid={`ob-cta-${step.key}`}
                  className="ml-2 flex-shrink-0 flex items-center gap-0.5 text-xs font-medium text-orange-500 hover:text-orange-600 whitespace-nowrap"
                >
                  {step.cta}
                  <ChevronRight size={12} />
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
